import React, { useState } from 'react';
import { Settings, Map, User, Users, Trophy, Calendar, Award, Activity, Timer, TrendingUp, ChevronRight, Plus } from 'lucide-react';
import Navigation from '../components/common/Navigation';
import ChallengeCard from '../components/tean/ChallengeCard';

const ChallengesPage = () => {
  const [activeTab, setActiveTab] = useState('active');

  // Sample data for challenges
  const [activeChallenges, setActiveChallenges] = useState([
    {
      id: 1,
      title: 'Spring Step Challenge',
      description: 'Complete 10,000 steps daily for 30 days',
      startDate: 'Mar 15, 2025',
      endDate: 'Apr 14, 2025',
      progress: 72,
      participants: 248,
      type: 'individual',
      icon: <Activity size={24} className="text-primary" />,
      status: 'on-track'
    },
    {
      id: 2,
      title: 'Weekend Warrior',
      description: 'Walk 25,000 steps each weekend for a month',
      startDate: 'Mar 22, 2025',
      endDate: 'Apr 20, 2025',
      progress: 50,
      participants: 156,
      type: 'individual',
      icon: <Trophy size={24} className="text-primary" />,
      status: 'behind'
    },
    {
      id: 3,
      title: 'Team Distance Challenge',
      description: 'Collaboratively walk 500km with your team',
      startDate: 'Mar 20, 2025',
      endDate: 'Apr 30, 2025',
      progress: 65,
      participants: 5,
      type: 'team',
      icon: <Users size={24} className="text-primary" />,
      status: 'on-track'
    }
  ]);

  const [availableChallenges, setAvailableChallenges] = useState([
    {
      id: 4,
      title: 'Early Bird Walker',
      description: 'Complete 5,000 steps before 9AM for 14 days',
      startDate: 'Apr 10, 2025',
      endDate: 'Apr 24, 2025',
      participants: 135,
      type: 'individual',
      icon: <Timer size={24} className="text-secondary" />,
      difficulty: 'Medium'
    },
    {
      id: 5,
      title: 'Stepping Up',
      description: 'Increase your daily step count by 500 each day for a week',
      startDate: 'Apr 15, 2025',
      endDate: 'Apr 22, 2025',
      participants: 87,
      type: 'individual',
      icon: <TrendingUp size={24} className="text-secondary" />,
      difficulty: 'Hard'
    },
    {
      id: 6,
      title: 'Global Walker',
      description: 'Join walkers worldwide to reach 1 million steps together',
      startDate: 'Apr 20, 2025',
      endDate: 'May 20, 2025',
      participants: 1256,
      type: 'global',
      icon: <Map size={24} className="text-secondary" />,
      difficulty: 'Easy'
    }
  ]);

  const [completedChallenges, setCompletedChallenges] = useState([
    {
      id: 7,
      title: 'Winter Walker',
      description: 'Complete 8,000 steps daily for 30 days',
      startDate: 'Jan 15, 2025',
      endDate: 'Feb 14, 2025',
      finalPosition: '42nd',
      participants: 312,
      type: 'individual',
      icon: <Award size={24} className="text-accent" />,
      reward: 'Silver Badge'
    },
    {
      id: 8,
      title: 'Valentine\'s Day Challenge',
      description: 'Walk a heart-shaped route on Valentine\'s Day',
      startDate: 'Feb 14, 2025',
      endDate: 'Feb 14, 2025',
      finalPosition: 'Completed',
      participants: 520,
      type: 'event',
      icon: <Calendar size={24} className="text-accent" />,
      reward: 'Heart Badge'
    }
  ]);

  // Function to join a challenge
  const joinChallenge = (challengeId) => {
    const challenge = availableChallenges.find(c => c.id === challengeId);
    if (challenge) {
      // Add to active challenges with 0 progress
      setActiveChallenges([...activeChallenges, {
        ...challenge,
        progress: 0,
        status: 'just-started'
      }]);
      
      // Remove from available challenges
      setAvailableChallenges(availableChallenges.filter(c => c.id !== challengeId));
    }
  };

  // Render active challenges
  const renderActiveChallenges = () => (
    <div className="space-y-4">
       {activeChallenges.map(challenge => (
      <ChallengeCard 
        key={challenge.id} 
        challenge={challenge} 
        type="active" 
      />
    ))}
    </div>
  );

  // Render available challenges
  const renderAvailableChallenges = () => (
    <div className="space-y-4">
       {availableChallenges.map(challenge => (
      <ChallengeCard 
        key={challenge.id} 
        challenge={challenge} 
        type="available" 
        onJoin={joinChallenge}
      />
    ))}
    </div>
  );

  // Render completed challenges
  const renderCompletedChallenges = () => (
    <div className="space-y-4">
       {completedChallenges.map(challenge => (
      <ChallengeCard 
        key={challenge.id} 
        challenge={challenge} 
        type="completed" 
      />
    ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-background-light">
      {/* Sidebar Navigation */}
      
        <nav className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-white shadow-sm z-20 flex-col">
      <Navigation />
      </nav>
{/*       
        
        <div className="mt-auto pt-4 border-t">
          <div className="flex items-center p-4">
            <img src="/api/placeholder/40/40" alt="Profile" className="w-10 h-10 rounded-full mr-3" />
            <div>
              <p className="font-medium">John Walker</p>
              <p className="text-sm text-gray-500">Premium Member</p>
            </div>
          </div>
        </div>
      </div> */}
      
      {/* Main Content */}
      <div className="max-w-7xl lg:ml-64 overflow-auto">
        <header className="bg-white p-4 shadow-sm flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Challenges</h1>
            <p className="text-gray-500">Join challenges and track your progress</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <span className="absolute top-0 right-0 h-2 w-2 bg-accent rounded-full"></span>
              <button className="p-2 rounded-full bg-gray-100">
                <Settings size={20} />
              </button>
            </div>
            <img src="/api/placeholder/40/40" alt="Profile" className="w-10 h-10 rounded-full" />
          </div>
        </header>
        
        <div className="p-6">
          {/* Challenge Stats */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center">
                <div className="p-2 bg-primary bg-opacity-10 rounded-full mr-3">
                  <Activity size={24} className="text-primary" />
                </div>
                <div>
                  <p className="text-gray-500">Active Challenges</p>
                  <p className="text-2xl font-bold">{activeChallenges.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center">
                <div className="p-2 bg-secondary bg-opacity-10 rounded-full mr-3">
                  <Trophy size={24} className="text-secondary" />
                </div>
                <div>
                  <p className="text-gray-500">Completed Challenges</p>
                  <p className="text-2xl font-bold">{completedChallenges.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center">
                <div className="p-2 bg-accent bg-opacity-10 rounded-full mr-3">
                  <Award size={24} className="text-accent" />
                </div>
                <div>
                  <p className="text-gray-500">Badges Earned</p>
                  <p className="text-2xl font-bold">5</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Hot Stepper Feature Promo */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-3 bg-accent bg-opacity-10 rounded-full mr-4">
                  <Users size={24} className="text-accent" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Hot Stepper</h2>
                  <p className="text-gray-500">Join a team challenge and compete with friends</p>
                </div>
              </div>
              <button className="bg-accent text-white px-4 py-2 rounded-md">
                Create Team
              </button>
            </div>
          </div>
          
          {/* Challenge Tabs */}
          <div className="mb-6 border-b">
            <div className="flex space-x-6">
              <button
                className={`pb-3 ${activeTab === 'active' ? 'text-primary border-b-2 border-primary font-medium' : 'text-gray-500'}`}
                onClick={() => setActiveTab('active')}
              >
                Active Challenges
              </button>
              <button
                className={`pb-3 ${activeTab === 'available' ? 'text-primary border-b-2 border-primary font-medium' : 'text-gray-500'}`}
                onClick={() => setActiveTab('available')}
              >
                Available Challenges
              </button>
              <button
                className={`pb-3 ${activeTab === 'completed' ? 'text-primary border-b-2 border-primary font-medium' : 'text-gray-500'}`}
                onClick={() => setActiveTab('completed')}
              >
                Completed
              </button>
            </div>
          </div>
          
          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'active' && renderActiveChallenges()}
            {activeTab === 'available' && renderAvailableChallenges()}
            {activeTab === 'completed' && renderCompletedChallenges()}
          </div>
        </div>
      </div>
      <div className="md:hidden block sticky bottom-0 z-50 bg-white border-t border-gray-200 p-2">
        <Navigation orientation='horizontal' />
      </div>
    </div>
  );
};

export default ChallengesPage;